import type { ListedArea } from 'contracts/Areas';
import type { ListedCarrier } from 'contracts/Carriers';
import type { ListedCompany } from 'contracts/Companies';
import type { ListedPhysicalDock } from 'contracts/PhysicalDocks';
import type { ListedOrderType } from 'contracts/OrderTypes';
import type { ListedVehicleType } from 'contracts/VehicleTypes';
import type {
  CreateStandaloneYardGateEntry,
  YardGateAppointmentDto,
  YardGateAppointmentsResponse,
  YardGateAppointmentType,
  YardGateAuthorizerDto,
  YardGateCatalogs,
  YardGateChecklistAttachmentsDto,
  YardGateChecklistAttachmentUrlDto,
  YardGateChangesResponse,
  YardGateDateRange,
  YardGateQueryFilters,
  YardGateMutableStatus,
  YardGateOption,
  YardGateSnapshotDto,
  UpdateYardGateContact,
  UpdateYardGateObservation,
  UpdateYardGateDocks,
} from 'contracts/YardGate';
import { api } from 'services';
import { Formatter } from 'utils';

const apiRoot = (api.defaults.baseURL || '').replace(/\/dashboard\/?$/, '');
const appointmentsPath = `${apiRoot}/api/v1/yms-portaria/gate-appointments`;
const appointmentDetailsPath = `${apiRoot}/api/v1/yms-portaria/appointments`;
const entryAuthorizersPath = `${apiRoot}/api/v1/yms-portaria/entry-authorizers`;
const portariaChecklistsPath = `${apiRoot}/api/v1/yms-portaria/portaria-checklists`;
const dateParams = ({
  dateFrom,
  dateTo,
}: YardGateDateRange): Record<string, string> => {
  const params: Record<string, string> = {};
  if (!dateFrom || !dateTo || dateFrom === dateTo) {
    const date = dateFrom || dateTo;
    if (date) params.date = date;
    return params;
  }
  params.startDate = dateFrom;
  params.endDate = dateTo;
  return params;
};

const responseItems = <T>(response: T[] | { data: T[] }): T[] =>
  Array.isArray(response) ? response : response.data;

const yardGateFilterParams = (filters: YardGateQueryFilters) => {
  const params: Record<string, string | number> = { ...dateParams(filters) };
  if (filters.statuses?.length === 1) {
    params.status = filters.statuses[0];
  } else if (filters.statuses && filters.statuses.length > 1) {
    params.statuses = filters.statuses.join(',');
  }
  if (filters.type) params.type = filters.type;
  if (filters.registrationType) params.registrationType = filters.registrationType;
  if (filters.driverCpf) params.driverCpf = filters.driverCpf;
  if (filters.driverName) params.driverName = filters.driverName;
  if (filters.phone) params.phone = filters.phone;
  if (filters.orderNumber) params.collectionOrder = filters.orderNumber;
  if (filters.orderTypeId) params.orderTypeId = filters.orderTypeId;
  if (filters.carrierId) params.carrierId = filters.carrierId;
  if (filters.customerId) params.companyId = filters.customerId;
  if (filters.warehouseIds?.length) {
    params.warehouseIds = filters.warehouseIds.join(',');
  }
  if (filters.plate) params.plate = filters.plate;
  if (filters.dock) params.dock = filters.dock;
  return params;
};

export const listYardGateSnapshot = async (
  filters: YardGateQueryFilters,
  page = 1,
  limit = 10
): Promise<YardGateSnapshotDto> => {
  const params = {
    ...yardGateFilterParams(filters),
    page,
    limit,
    orderBy: filters.orderBy ?? 'date',
    direction: filters.direction ?? 'asc',
  };
  const first = await api.get<YardGateAppointmentsResponse>(appointmentsPath, {
    params,
  });

  return {
    data: first.data.data,
    trackedStepColumns: first.data.meta.trackedStepColumns,
    syncCursor: first.data.meta.syncCursor,
    pagination: {
      total: first.data.meta.total,
      page: first.data.meta.currentPage,
      perPage: first.data.meta.perPage,
      lastPage: first.data.meta.lastPage,
    },
  };
};

export const getYardGateChanges = async (
  filters: YardGateQueryFilters,
  afterId: number
): Promise<YardGateChangesResponse> => {
  const { data } = await api.get<YardGateChangesResponse>(
    `${appointmentsPath}/changes`,
    { params: { ...yardGateFilterParams(filters), afterId } }
  );
  return data;
};

export const getYardGateCatalogs = async (): Promise<YardGateCatalogs> => {
  const [orderTypes, carriers, vehicleTypes, companies, areas, docks] =
    await Promise.all([
      api.get<ListedOrderType[] | { data: ListedOrderType[] }>('order-types', {
        params: { asList: 1, orderBy: 'name', direction: 'asc' },
      }),
      api.get<ListedCarrier[] | { data: ListedCarrier[] }>('carriers', {
        params: { asList: 1, orderBy: 'tradeName', direction: 'asc' },
      }),
      api.get<ListedVehicleType[] | { data: ListedVehicleType[] }>(
        'vehicle-types',
        { params: { asList: 1, orderBy: 'name', direction: 'asc' } }
      ),
      api.get<ListedCompany[] | { data: ListedCompany[] }>('companies', {
        params: { asList: 1, orderBy: 'tradeName', direction: 'asc' },
      }),
      api.get<ListedArea[] | { data: ListedArea[] }>('areas', {
        params: { asList: 1, orderBy: 'name', direction: 'asc' },
      }),
      api.get<ListedPhysicalDock[] | { data: ListedPhysicalDock[] }>(
        'docks-physical',
        {
          params: { asList: 1, orderBy: 'building', direction: 'asc' },
        }
      ),
    ]);

  return {
    orderTypes: responseItems(orderTypes.data).map(({ id, name }) => ({
      value: id,
      label: name,
    })),
    carriers: responseItems(carriers.data).map(({ id, tradeName }) => ({
      value: id,
      label: tradeName,
    })),
    vehicleTypes: responseItems(vehicleTypes.data).map(({ id, name }) => ({
      value: id,
      label: name,
    })),
    customers: responseItems(companies.data).map(
      ({ id, tradeName, document, documentType }) => ({
        value: id,
        label: tradeName,
        document: Formatter.document(document, documentType),
      })
    ),
    areas: responseItems(areas.data).map(({ id, name }) => ({
      value: id,
      label: name,
    })),
    docks: responseItems(docks.data).map(
      ({ id, warehouseId, name, building }) => {
        const condition = building.condition
          ? ` (${building.condition.charAt(0)}${building.condition
              .slice(1)
              .toLocaleLowerCase('pt-BR')})`
          : '';

        return {
          value: id,
          warehouseId,
          label: `${name} - ${building.name}${condition}`,
        };
      }),
  };
};

export const getYardGateAuthorizers = async (): Promise<YardGateOption[]> => {
  const response = await api.get<
    YardGateAuthorizerDto[] | { data: YardGateAuthorizerDto[] }
  >(entryAuthorizersPath, {
    params: { asList: 1, orderBy: 'name', direction: 'asc' },
  });

  return responseItems(response.data).map(({ id, name }) => ({
    value: id,
    label: name,
  }));
};

export const createYardGateAuthorizer = async (
  name: string
): Promise<YardGateOption> => {
  const { data } = await api.post<YardGateAuthorizerDto>(entryAuthorizersPath, {
    name,
  });
  return { value: data.id, label: data.name };
};

export const createStandaloneYardGateEntry = async (
  payload: CreateStandaloneYardGateEntry
): Promise<YardGateAppointmentDto> => {
  const { data } = await api.post<YardGateAppointmentDto>(
    `${appointmentsPath}/standalone`,
    payload
  );
  return data;
};

const appointmentTypePath = (type: YardGateAppointmentType): string =>
  type === 'ORDER' ? 'orders' : 'standalone';

export const updateYardGateStatus = async (
  type: YardGateAppointmentType,
  sourceId: number,
  status: YardGateMutableStatus
): Promise<YardGateAppointmentDto> => {
  const { data } = await api.patch<YardGateAppointmentDto>(
    `${appointmentsPath}/${appointmentTypePath(type)}/${sourceId}/status`,
    { status }
  );
  return data;
};

export const updateYardGateContact = async (
  type: YardGateAppointmentType,
  sourceId: number,
  payload: UpdateYardGateContact
): Promise<YardGateAppointmentDto> => {
  const { data } = await api.patch<YardGateAppointmentDto>(
    `${appointmentDetailsPath}/${appointmentTypePath(type)}/${sourceId}`,
    payload
  );
  return data;
};

export const updateYardGateObservation = async (
  type: YardGateAppointmentType,
  sourceId: number,
  payload: UpdateYardGateObservation
): Promise<YardGateAppointmentDto> => {
  const { data } = await api.patch<YardGateAppointmentDto>(
    `${appointmentDetailsPath}/${appointmentTypePath(type)}/${sourceId}`,
    payload
  );
  return data;
};

export const updateYardGateDocks = async (
  type: YardGateAppointmentType,
  sourceId: number,
  payload: UpdateYardGateDocks
): Promise<YardGateAppointmentDto> => {
  const { data } = await api.patch<YardGateAppointmentDto>(
    `${appointmentsPath}/${appointmentTypePath(type)}/${sourceId}/docks`,
    payload
  );
  return data;
};

const yardGateChecklistTargetPath = (
  type: YardGateAppointmentType,
  sourceId: number
): string =>
  `${portariaChecklistsPath}/attachments/${appointmentTypePath(type)}/${sourceId}`;

export const listYardGateChecklistAttachments = async (
  type: YardGateAppointmentType,
  sourceId: number
): Promise<YardGateChecklistAttachmentsDto> => {
  const { data } = await api.get<YardGateChecklistAttachmentsDto>(
    yardGateChecklistTargetPath(type, sourceId)
  );
  return data;
};

export const uploadYardGateChecklistAttachments = async (
  type: YardGateAppointmentType,
  sourceId: number,
  files: File[]
): Promise<YardGateChecklistAttachmentsDto> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files[]', file, file.name));
  const { data } = await api.post<YardGateChecklistAttachmentsDto>(
    yardGateChecklistTargetPath(type, sourceId),
    formData
  );
  return data;
};

export const getYardGateChecklistAttachmentUrl = async (
  type: YardGateAppointmentType,
  sourceId: number,
  attachmentId: number
): Promise<YardGateChecklistAttachmentUrlDto> => {
  const { data } = await api.get<YardGateChecklistAttachmentUrlDto>(
    `${yardGateChecklistTargetPath(type, sourceId)}/${attachmentId}/url`
  );
  return data;
};

export const deleteYardGateChecklistAttachment = async (
  type: YardGateAppointmentType,
  sourceId: number,
  attachmentId: number
): Promise<void> => {
  await api.delete(
    `${yardGateChecklistTargetPath(type, sourceId)}/${attachmentId}`
  );
};

export function useYardGate() {
  return {
    listYardGateSnapshot,
    getYardGateChanges,
    getYardGateCatalogs,
    getYardGateAuthorizers,
    createYardGateAuthorizer,
    createStandaloneYardGateEntry,
    updateYardGateStatus,
    updateYardGateContact,
    updateYardGateDocks,
    updateYardGateObservation,
    listYardGateChecklistAttachments,
    uploadYardGateChecklistAttachments,
    getYardGateChecklistAttachmentUrl,
    deleteYardGateChecklistAttachment,
  };
}
